import React, { useState, useEffect, useMemo } from "react";
import { Container } from "./styled";

const StatHistory = ({ appData, activeProfile, setIsLoaded }) => {
  const [games, setGames] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Función para obtener estadísticas de todas las temporadas
    const fetchAllSeasonsData = async () => {
      const activeLeague = appData?.teams?.find(
        (e) => e._id === activeProfile._id
      ).league;
      if (!activeLeague) {
        console.error("No active league found.");
        return;
      }
      try {
        const allSeasonGames = await Promise.all(
          activeProfile.history.map(async (season) => {
            const response = await fetch(
              `https://deportactico-vercel-serverless.vercel.app/api/gamesByleague?season=${season.season}&league=${season.league}`
            );
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
          })
        );
        setGames(allSeasonGames.flat());
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllSeasonsData();
  }, [appData, activeProfile]);

  // Función para procesar las estad
  const calculateTeamPosition = (season, league) => {
    let teamsArr = [];

    appData.teams?.forEach((team) => {
      const hasPlayedInSameLeague = team.history?.some(
        (entry) => entry.season === season && entry.league === league
      );
      if (hasPlayedInSameLeague) {
        teamsArr.push({
          _id: team._id,
          name: team.name,
          gamesWon: 0,
          gamesLost: 0,
          totalPoints: 0,
          pointsMade: 0,
          pointsRecibed: 0,
          zone: team.zone === "sur" ? "sur" : "norte",
        });
      }
    });
    for (let index = 0; index < games?.length; index++) {
      if (
        games[index].category === league &&
        games[index].season === season &&
        games[index].stage === "Fase regular"
      ) {
        let localTotalPts = 0;
        let visitTotalPts = 0;
        const localTeam = teamsArr?.find(
          (object) => object._id === games[index].local_team
        );
        games[index].local_stats.forEach((element) => {
          localTotalPts += element.pts;
        });
        const visitTeam = teamsArr?.find(
          (object) => object._id === games[index].visit_team
        );
        games[index].visit_stats.forEach((element) => {
          visitTotalPts += element.pts;
        });
        localTeam.pointsMade += localTotalPts;
        localTeam.pointsRecibed += visitTotalPts;
        visitTeam.pointsMade += visitTotalPts;
        visitTeam.pointsRecibed += localTotalPts;
        if (localTotalPts > visitTotalPts) {
          localTeam.gamesWon++;
          visitTeam.gamesLost++;
        } else {
          localTeam.gamesLost++;
          visitTeam.gamesWon++;
        }
      }
    }
    teamsArr.forEach((element) => {
      element.totalPoints = element.gamesWon * 2 + element.gamesLost;
    });
    // Ordenar equipos
    teamsArr.sort(function (a, b) {
      return (
        b.totalPoints - a.totalPoints ||
        b.gamesWon - a.gamesWon ||
        a.gamesLost - b.gamesLost ||
        b.pointsMade - a.pointsMade ||
        a.pointsRecibed - b.pointsRecibed
      );
    });

    if (
      teamsArr[
        teamsArr.findIndex((object) => {
          return object._id === activeProfile._id;
        })
      ].gamesWon === 0 &&
      teamsArr[
        teamsArr.findIndex((object) => {
          return object._id === activeProfile._id;
        })
      ].gamesLost === 0
    ) {
      return { gamesWon: 0, gamesLost: 0, winingPercentage: 0, position: 0 };
    } else {
      const indexOfObject =
        league === "LA"
          ? teamsArr
              .filter(
                (e) =>
                  e.zone ===
                  teamsArr.filter((e) => e._id === activeProfile._id)[0].zone
              )
              .findIndex((object) => {
                return object._id === activeProfile._id;
              })
          : teamsArr.findIndex((object) => {
              return object._id === activeProfile._id;
            });
      return {
        gamesWon:
          teamsArr[
            teamsArr.findIndex((object) => {
              return object._id === activeProfile._id;
            })
          ].gamesWon,
        gamesLost:
          teamsArr[
            teamsArr.findIndex((object) => {
              return object._id === activeProfile._id;
            })
          ].gamesLost,
        winingPercentage: Math.round(
          (teamsArr[
            teamsArr.findIndex((object) => {
              return object._id === activeProfile._id;
            })
          ].gamesWon /
            (teamsArr[
              teamsArr.findIndex((object) => {
                return object._id === activeProfile._id;
              })
            ].gamesWon +
              teamsArr[
                teamsArr.findIndex((object) => {
                  return object._id === activeProfile._id;
                })
              ].gamesLost)) *
            100
        ),
        position: indexOfObject + 1,
      };
    }
  };

  // Memorizar los cálculos de posiciones del equipo por temporada
  const teamStatsBySeason = useMemo(() => {
    if (!games || !appData) return [];
    return activeProfile.history.map((item) => {
      const { season, league } = item;
      const stats = calculateTeamPosition(season, league);
      return { ...item, ...stats };
    });
  }, [games, appData, activeProfile]);

  return (
    <Container>
      <div className="history-header">
        <p>Temporada</p>
        <p>Nombre</p>
        <p>Liga</p>
        <p>G</p>
        <p>P</p>
        <p>G%</p>
        <p>Posición</p>
      </div>
      {!isLoading &&
        teamStatsBySeason
          .sort((a, b) => {
            const [startYearA] = a.season.split("/").map(Number);
            const [startYearB] = b.season.split("/").map(Number);
            return startYearB - startYearA;
          })
          .map((item, index) => (
            <div className="history-content">
              <p>{item.season}</p>
              <p>{activeProfile?.name}</p>
              <p>{item.league}</p>
              <p>{calculateTeamPosition(item.season, item.league).gamesWon}</p>
              <p>{calculateTeamPosition(item.season, item.league).gamesLost}</p>
              <p>
                {
                  calculateTeamPosition(item.season, item.league)
                    .winingPercentage
                }
              </p>
              <p>{calculateTeamPosition(item.season, item.league).position}</p>
            </div>
          ))}
      {isLoading ? <h1>Cargando</h1> : null}
    </Container>
  );
};

export default StatHistory;
